import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spin, Alert, Button } from 'antd';
import { Refresh, LogoutOutlined } from '@mui/icons-material';
import useSessionValidation from '../hooks/useSessionValidation';
import sessionValidationService from '../services/sessionValidationService';

/**
 * Session validation component that handles session state and provides user feedback
 */
const SessionValidator = ({ children, showValidationStatus = false }) => {
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const user = useSelector(state => state.auth.user);
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);

  const {
    isValidating,
    lastValidation,
    validationError,
    hasValidSession,
    validateSession,
    forceValidateSession
  } = useSessionValidation({
    validateOnMount: true,
    periodicValidation: true
  });

  // Handle validation errors
  useEffect(() => {
    if (validationError) {
      setErrorMessage(validationError.message || 'Session validation failed');
      setShowError(true);
    }
  }, [validationError]);

  // Auto-hide errors after 5 seconds
  useEffect(() => {
    if (showError) {
      const timer = setTimeout(() => setShowError(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showError]);

  // Show validation error banner
  if (showError && showValidationStatus) {
    return (
      <div style={{ padding: '16px' }}>
        <Alert
          message="Session Validation Error"
          description={errorMessage}
          type="error"
          showIcon
          action={
            <Button
              size="small"
              danger
              icon={<LogoutOutlined />}
              onClick={() => sessionValidationService._handleInvalidSession()}
            >
              Logout
            </Button>
          }
          closable
          onClose={() => setShowError(false)}
        />
        {children}
      </div>
    );
  }

  // Show validation in progress
  if (isValidating && showValidationStatus) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          Validating session...
        </div>
        {children}
      </div>
    );
  }

  // Show session status if enabled
  if (showValidationStatus && isLoggedIn && lastValidation) {
    return (
      <div style={{ padding: '16px' }}>
        <Alert
          message="Session Status"
          description={`Last validated: ${lastValidation.toLocaleTimeString()}`}
          type={hasValidSession ? "success" : "warning"}
          showIcon
          action={
            <Button
              size="small"
              icon={<Refresh />}
              onClick={forceValidateSession}
            >
              Revalidate
            </Button>
          }
        />
        {children}
      </div>
    );
  }

  // Default: just render children
  return children;
};

export default SessionValidator;
